import { Skeleton, useMediaQuery } from "@mui/material";
import styles from "./list.module.css";

/**
 * Skeleton loader for the Booking List page.
 * Mirrors the real layout (summary grid, filter bar, country/status tags,
 * and either the desktop table or the mobile card list) so there's no
 * layout shift once data arrives.
 *
 * Usage in list.jsx:
 *   import BookingListLoader from "./BookingListLoader";
 *   ...
 *   {contentLoading && <BookingListLoader />}
 */
const BookingListLoader = ({ rows = 8 }) => {
    const isMobile = useMediaQuery("(max-width:768px)");

    return (
        <div className={styles.MainCard}>
            {/* Summary grid */}
            <div className={styles.SummaryGrid}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <div className={styles.SummaryCard} key={`summary-${i}`}>
                        <Skeleton variant="text" width={isMobile ? 50 : 70} height={40} />
                        <Skeleton variant="text" width={isMobile ? 70 : 90} height={20} />
                    </div>
                ))}
            </div>

            {/* Filter bar */}
            <div className={styles.FilterBar}>
                <div className={styles.SearchBox} style={{ flex: isMobile ? "1 1 100%" : undefined }}>
                    <Skeleton variant="rounded" height={40} width="100%" />
                </div>
                <div className={styles.FilterItem} style={{ width: isMobile ? "48%" : 180 }}>
                    <Skeleton variant="rounded" height={40} width="100%" />
                </div>
                <div className={styles.FilterItem} style={{ width: isMobile ? "48%" : 220 }}>
                    <Skeleton variant="rounded" height={40} width="100%" />
                </div>
            </div>

            {/* Status/country tag legend - hidden on mobile to save space, same as it's cramped there */}
            {!isMobile && (
                <div className={styles.CoutryTagSec}>
                    {Array.from({ length: 9 }).map((_, i) => (
                        <div className={styles.CoutryTag} key={`tag-${i}`}>
                            <Skeleton variant="circular" width={14} height={14} />
                            <Skeleton variant="text" width={70} style={{ marginLeft: 6 }} />
                        </div>
                    ))}
                </div>
            )}

            {/* Main content: table skeleton on desktop, cards skeleton on mobile */}
            {isMobile ? (
                <div className={styles.MobileCards}>
                    {Array.from({ length: rows }).map((_, i) => (
                        <MobileBookingCardSkeleton key={`mcard-${i}`} />
                    ))}
                </div>
            ) : (
                <DesktopTableSkeleton rows={rows} />
            )}

            {/* Pagination */}
            <div className={styles.PaginationWrap} style={{ display: "flex", justifyContent: isMobile ? "center" : "flex-end", padding: "12px 0" }}>
                <Skeleton variant="rounded" width={isMobile ? "100%" : 320} height={40} />
            </div>
        </div>
    );
};

const DesktopTableSkeleton = ({ rows }) => {
    const columns = [
        "Booking ID",
        "Customer",
        "Order Date",
        "Total",
        "Paid",
        "Due",
        "Payment",
        "Status",
        "Pickup Postcode",
        "Pickup Date",
        "Created By",
        "Actions",
    ];

    return (
        <div className={styles.TableCard}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th key={col} style={{ textAlign: "left", padding: "12px 16px" }}>
                                <Skeleton variant="text" width={col.length > 10 ? 90 : 60} />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <tr key={`row-${rowIndex}`}>
                            {columns.map((col, colIndex) => (
                                <td key={`cell-${rowIndex}-${colIndex}`} style={{ padding: "12px 16px" }}>
                                    {col === "Status" ? (
                                        <Skeleton variant="rounded" width={90} height={24} />
                                    ) : col === "Payment" ? (
                                        <Skeleton variant="rounded" width={70} height={24} />
                                    ) : col === "Actions" ? (
                                        <div style={{ display: "flex", justifyContent: "center" }}>
                                            <Skeleton variant="circular" width={28} height={28} />
                                        </div>
                                    ) : (
                                        <Skeleton variant="text" width={col.length > 10 ? 90 : 60} />
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const MobileBookingCardSkeleton = () => (
    <div
        style={{
            border: "1px solid #eee",
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
        }}
    >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <Skeleton variant="text" width={90} height={26} />
            <Skeleton variant="rounded" width={80} height={24} />
        </div>

        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
            <Skeleton variant="text" width={70} />
            <Skeleton variant="text" width={70} />
            <Skeleton variant="text" width={70} />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <Skeleton variant="rounded" width={70} height={22} />
            <Skeleton variant="circular" width={28} height={28} />
        </div>
    </div>
);

export default BookingListLoader;